import { useLayoutEffect, useRef } from 'react';
import { reelVisibleAdjacentCardOffset } from '../config/config';

/**
 * Handles scrolling to an initial reel item on load and returns a scroll to item function.
 */
const useReelScrollTo = (reelRef: React.MutableRefObject<HTMLUListElement | null>, gapSize: number) => {
    const hasDoneInitialScroll = useRef(false);
    const timeout = useRef<NodeJS.Timeout | undefined>();

    // Scrolls to a reel child using it's index value.
    const scrollToItem = (index: number) => {
        if (!reelRef.current || !reelRef.current.children.length || !reelRef.current.children[index]) {
            return;
        }

        reelRef.current.scrollTo({
            top: 0,
            left: (reelRef.current.children[index] as HTMLElement).offsetLeft,
            behavior: "smooth",
        });
    };

    // Sets the initial scroll position on load if there is an initial real item to be focused on.
    useLayoutEffect(() => {
        if (!reelRef.current || hasDoneInitialScroll.current) {
            return () => {};
        }

        const initialItemToScrollTo = reelRef.current?.querySelector('[data-initial-reel-item=true]');

        if (initialItemToScrollTo) {
            // Find the direct child item of the reel that contains the initial scroll to element.
            const child = Array.from(reelRef.current.children).find((child) => (
                child.contains(initialItemToScrollTo)
            )) as HTMLElement | undefined;

            if (child) {
                // offsetLeft needs to be stored in a variable for scrollTo to work in Safari correctly.
                const left = child.offsetLeft - (reelVisibleAdjacentCardOffset + gapSize);

                // Timeout is needed for initial scrollTo to work in Safari correctly.
                timeout.current = setTimeout(() => {
                    reelRef.current!.scrollTo({
                        left,
                        behavior: 'instant',
                    });
                }, 25);
            }
        }

        // Makes sure this effect only runs once.
        hasDoneInitialScroll.current = true;

        // Clears timeout on unmount.
        return () => {
            clearTimeout(timeout.current);
            timeout.current = undefined;
        }
    }, [reelRef, gapSize]);

    return scrollToItem;
};

export default useReelScrollTo;
